import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, AtSign, Loader2, Mail, Plus, Save, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Notification from "../../../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiGet, apiPut } from "../../../../apiService";
import { getErrorMessage } from "../../../../utils/errorHelper";

/* ===================== STYLES (shared with Help Desk Config) ===================== */
import styles from "../../AdminHelpDesk/Config/HelpDeskConfig.module.scss";

const BACK_PATH = "/admin/contact-requests";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const EmailChipsField = ({ label, value, onChange, error, placeholder }) => {
  const [draft, setDraft] = useState("");
  const [localError, setLocalError] = useState("");

  const addEmail = () => {
    const next = draft.trim();
    if (!next) return;
    if (!EMAIL_REGEX.test(next)) {
      setLocalError("Enter a valid email address");
      return;
    }
    if (value.includes(next)) {
      setLocalError("Email already added");
      return;
    }
    onChange([...value, next]);
    setDraft("");
    setLocalError("");
  };

  const removeEmail = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  };

  return (
    <div className={styles["helpdesk-config__chips-field"]}>
      <label className={styles["helpdesk-config__field-label"]}>{label}</label>

      <div className={styles["helpdesk-config__chip-input-row"]}>
        <input
          type="email"
          value={draft}
          placeholder={placeholder || "Add an email and press Enter"}
          onChange={(e) => {
            setDraft(e.target.value);
            setLocalError("");
          }}
          onKeyDown={handleKeyDown}
          className={styles["helpdesk-config__chip-input"]}
        />
        <button
          type="button"
          className={styles["helpdesk-config__chip-add"]}
          onClick={addEmail}
          aria-label="Add email"
        >
          <Plus size={16} strokeWidth={2.5} />
        </button>
      </div>

      {(error || localError) && (
        <p className={styles["helpdesk-config__error-text"]}>
          {error || localError}
        </p>
      )}

      {value.length > 0 && (
        <div className={styles["helpdesk-config__chips"]}>
          {value.map((email, idx) => (
            <span
              key={`${email}-${idx}`}
              className={styles["helpdesk-config__chip"]}
            >
              <AtSign size={12} strokeWidth={2.5} />
              {email}
              <button
                type="button"
                className={styles["helpdesk-config__chip-remove"]}
                onClick={() => removeEmail(idx)}
                aria-label={`Remove ${email}`}
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const ContactUsConfig = () => {
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [sendAckToRequester, setSendAckToRequester] = useState(true);
  const [requireEmailOtp, setRequireEmailOtp] = useState(false);

  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet("catalog", "/contact-us/config");
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load config",
          }),
        );
      }
      const data = res.data || {};
      setConfig(data);
      setToEmails(Array.isArray(data.toEmails) ? data.toEmails : []);
      setCcEmails(Array.isArray(data.ccEmails) ? data.ccEmails : []);
      setBccEmails(Array.isArray(data.bccEmails) ? data.bccEmails : []);
      setIsActive(typeof data.isActive === "boolean" ? data.isActive : true);
      setSendAckToRequester(
        typeof data.sendAckToRequester === "boolean"
          ? data.sendAckToRequester
          : true,
      );
      setRequireEmailOtp(
        typeof data.requireEmailOtp === "boolean" ? data.requireEmailOtp : false,
      );
    } catch (err) {
      showNotification(getErrorMessage(err, "Failed to load config"), "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const validate = () => {
    const next = {};
    if (toEmails.length === 0) {
      next.toEmails = "At least one TO email is required";
    }
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    setSubmitting(true);
    try {
      const payload = {
        toEmails,
        ccEmails,
        bccEmails,
        isActive,
        sendAckToRequester,
        requireEmailOtp,
      };
      const res = await apiPut("catalog", "/contact-us/config", payload);
      if (res?.status === "success") {
        showNotification(
          res?.message || "Config updated successfully",
          "success",
        );
        setConfig(res.data || {});
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to update config",
          }),
        );
      }
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to update config"),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles["helpdesk-config__page"]}>
      {notification && (
        <div className={styles["helpdesk-config__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      <div className={styles["helpdesk-config__header"]}>
        <Link to={BACK_PATH} className={styles["helpdesk-config__back-btn"]}>
          <ArrowLeft size={20} strokeWidth={2.5} />
        </Link>
        <div className={styles["helpdesk-config__title-group"]}>
          <h2 className={styles["helpdesk-config__title"]}>
            Contact-Us Configuration
          </h2>
          <p className={styles["helpdesk-config__subtitle"]}>
            Email routing used when a visitor submits the contact-us form.
          </p>
        </div>
      </div>

      {loading ? (
        <div className={styles["helpdesk-config__loading"]}>
          <Loader2 className={styles["helpdesk-config__spin"]} size={28} />
          <span>Loading config…</span>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <div className={styles["helpdesk-config__card"]}>
            <div className={styles["helpdesk-config__card-header"]}>
              <div className={styles["helpdesk-config__card-icon"]}>
                <AtSign size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["helpdesk-config__card-title"]}>
                Email Routing
              </h3>
            </div>

            <div className={styles["helpdesk-config__email-grid"]}>
              <EmailChipsField
                label="TO Emails"
                value={toEmails}
                onChange={(next) => {
                  setToEmails(next);
                  setErrors((prev) => ({ ...prev, toEmails: "" }));
                }}
                error={errors.toEmails}
                placeholder="support@doql.com"
              />
              <EmailChipsField
                label="CC Emails"
                value={ccEmails}
                onChange={setCcEmails}
                placeholder="lead@doql.com"
              />
              <EmailChipsField
                label="BCC Emails"
                value={bccEmails}
                onChange={setBccEmails}
                placeholder="audit@doql.com"
              />
            </div>
          </div>

          <div className={styles["helpdesk-config__card"]}>
            <div className={styles["helpdesk-config__card-header"]}>
              <div className={styles["helpdesk-config__card-icon"]}>
                <Mail size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["helpdesk-config__card-title"]}>
                Email Delivery
              </h3>
            </div>

            <label className={styles["helpdesk-config__checkbox-row"]}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className={styles["helpdesk-config__checkbox"]}
              />
              <span className={styles["helpdesk-config__checkbox-text"]}>
                <strong>Send contact-us submissions over email</strong>
                <small>
                  When enabled, every new contact-us submission is emailed to
                  the recipients configured above. Disable to stop email
                  delivery while still capturing requests in the dashboard.
                </small>
              </span>
            </label>

            <label className={styles["helpdesk-config__checkbox-row"]}>
              <input
                type="checkbox"
                checked={sendAckToRequester}
                onChange={(e) => setSendAckToRequester(e.target.checked)}
                className={styles["helpdesk-config__checkbox"]}
              />
              <span className={styles["helpdesk-config__checkbox-text"]}>
                <strong>Send a acknowledgement email to the requester</strong>
                <small>
                  When enabled, the person who submitted the contact-us form
                  receives an acknowledgement email confirming their request was
                  received.
                </small>
              </span>
            </label>

            <label className={styles["helpdesk-config__checkbox-row"]}>
              <input
                type="checkbox"
                checked={requireEmailOtp}
                onChange={(e) => setRequireEmailOtp(e.target.checked)}
                className={styles["helpdesk-config__checkbox"]}
              />
              <span className={styles["helpdesk-config__checkbox-text"]}>
                <strong>Require email OTP verification</strong>
                <small>
                  When enabled, visitors must verify their email with a one-time
                  code before their contact-us submission is accepted.
                </small>
              </span>
            </label>

            {config?.updatedAt && (
              <div className={styles["helpdesk-config__meta"]}>
                <span>Last updated</span>
                <strong>{formatDateTime(config.updatedAt)}</strong>
              </div>
            )}
          </div>

          <div className={styles["helpdesk-config__actions"]}>
            <button
              type="button"
              className={`${styles["helpdesk-config__btn"]} ${styles["helpdesk-config__btn--secondary"]}`}
              onClick={() => navigate(BACK_PATH)}
              disabled={submitting}
            >
              <X size={16} strokeWidth={2.5} />
              Cancel
            </button>

            <button
              type="submit"
              className={`${styles["helpdesk-config__btn"]} ${styles["helpdesk-config__btn--primary"]}`}
              disabled={submitting}
            >
              <Save size={16} strokeWidth={2.5} />
              {submitting ? "Saving…" : "Save Config"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ContactUsConfig;
